import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { MarkdownTransformer } from '@atlaskit/editor-markdown-transformer';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { ADFEncoder, ReactRenderer } from '@atlaskit/renderer';
import { token } from '@atlaskit/tokens';

import { AgentTextareaField } from '../../../../../common/ui/agent-textarea-field/index.tsx';
import type { AgentDetailsWithAggData } from '../../../../../controllers/agent-details/index.ts';

import { messages } from './messages.ts';

type AgentInstructionsProps = {
	agent?: AgentDetailsWithAggData; // cleanup with studio-gsn-and-scenario-refresh flag
	instructions?: string;
	onCopy: (instructions: string) => void;
	fullHeightText?: boolean;
};

const styles = cssMap({
	contentWrapper: {
		boxShadow: token('elevation.shadow.raised'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.small'),
	},
});

const adfEncoder = new ADFEncoder((schema) => new MarkdownTransformer(schema));

export const AgentInstructions = ({
	agent,
	instructions,
	onCopy,
	fullHeightText = true,
}: AgentInstructionsProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const defaultScenario = agent?.scenarios?.find((scenario) => scenario.isDefault);

	const instructionsContent = fg('studio-gsn-and-scenario-refresh')
		? instructions
		: defaultScenario?.instructions;

	if (fg('agent_studio_instructions_editor_enabled')) {
		if (!instructionsContent) {
			return (
				<Box xcss={styles.contentWrapper}>
					<Text color="color.text.subtlest">
						{formatMessage(messages.noInstructionPlaceholder)}
					</Text>
				</Box>
			);
		}
		const adfDoc = adfEncoder.encode(instructionsContent);

		return (
			<Box xcss={styles.contentWrapper}>
				<ReactRenderer document={adfDoc} />
			</Box>
		);
	}

	return (
		<AgentTextareaField
			value={instructionsContent}
			onCopy={onCopy}
			fullHeightText={fullHeightText}
			noValuePlaceholder={formatMessage(messages.noInstructionPlaceholder)}
		/>
	);
};
